import React from 'react';
import { PLAY_STORE_URL, APP_STORE_URL } from '../../legal/appLinks';

/* ============================================================================
   Home-page illustration set + app-store badges.

   All artwork is self-generated SVG (no external image assets — the build
   blocks remote hosts) drawn in the MainEvents brand gradient
   (blue #5B8FE8 → purple #6C3DD3). Scenes depict stylised people and phone
   mock-ups so the landing page reads like the app in real life without
   shipping photographs. Every gradient id is unique so multiple scenes can
   coexist on one page.
   ========================================================================== */

const BLUE = '#5B8FE8';
const PURPLE = '#6C3DD3';
const SKIN_A = '#F1C9A5';
const SKIN_B = '#C68642';

type ArtProps = { className?: string };

// Shared <defs> gradient — pass a unique id per illustration.
const grad = (id: string) => (
    <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={BLUE} />
        <stop offset="100%" stopColor={PURPLE} />
    </linearGradient>
);

/* ───────────────────────────── HERO ─────────────────────────────────────
   A woman holding a phone (event feed on screen) with a man beside her and
   floating event motifs (ticket, pin, calendar, chat). Wide, decorative. */
export function HomeHeroArt({ className }: ArtProps) {
    return (
        <svg viewBox="0 0 720 520" className={className} role="img" aria-label="Main Events" preserveAspectRatio="xMidYMid meet">
            <defs>
                {grad('home-hero-g')}
                <linearGradient id="home-hero-sky" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#eef3fd" />
                    <stop offset="100%" stopColor="#f3eefb" />
                </linearGradient>
            </defs>

            {/* soft backdrop */}
            <circle cx="360" cy="250" r="240" fill="url(#home-hero-sky)" />
            <circle cx="565" cy="120" r="70" fill={BLUE} opacity="0.10" />
            <circle cx="150" cy="150" r="46" fill={PURPLE} opacity="0.10" />

            {/* floating motifs */}
            <g opacity="0.95">
                {/* ticket */}
                <g transform="rotate(-10 560 210)">
                    <rect x="500" y="176" width="120" height="64" rx="12" fill="#fff" stroke={BLUE} strokeWidth="2" />
                    <line x1="576" y1="182" x2="576" y2="234" stroke={BLUE} strokeWidth="2" strokeDasharray="3 5" />
                    <circle cx="520" cy="208" r="6" fill={PURPLE} opacity="0.4" />
                    <circle cx="540" cy="208" r="6" fill={PURPLE} opacity="0.4" />
                    <circle cx="598" cy="208" r="12" fill="url(#home-hero-g)" opacity="0.35" />
                </g>
                {/* map pin */}
                <g transform="translate(120 250)">
                    <path d="M0 0 C0 -26 40 -26 40 0 C40 22 20 40 20 52 C20 40 0 22 0 0 Z" fill="url(#home-hero-g)" />
                    <circle cx="20" cy="2" r="9" fill="#fff" />
                </g>
                {/* chat bubble */}
                <g transform="translate(150 90)">
                    <rect x="0" y="0" width="86" height="58" rx="16" fill="#fff" stroke={PURPLE} strokeWidth="2" />
                    <path d="M18 58 L18 74 L38 58 Z" fill="#fff" />
                    <circle cx="26" cy="29" r="4" fill={BLUE} />
                    <circle cx="43" cy="29" r="4" fill={PURPLE} />
                    <circle cx="60" cy="29" r="4" fill={BLUE} />
                </g>
            </g>

            {/* man (left, slightly behind) */}
            <g transform="translate(214 214)">
                <rect x="-6" y="96" width="96" height="150" rx="30" fill={PURPLE} />
                <circle cx="42" cy="52" r="42" fill={SKIN_B} />
                <path d="M2 44 a40 40 0 0 1 80 0 v-6 a40 40 0 0 0 -80 0 Z" fill="#3a2a1a" />
                <rect x="20" y="150" width="44" height="8" rx="4" fill="#fff" opacity="0.5" />
            </g>

            {/* woman (front, holding phone) */}
            <g transform="translate(322 176)">
                <rect x="0" y="132" width="118" height="212" rx="36" fill="url(#home-hero-g)" />
                <circle cx="60" cy="70" r="52" fill={SKIN_A} />
                {/* hair */}
                <path d="M6 74 a54 54 0 0 1 108 0 c0 30 -12 44 -12 44 l-6 -34 a48 48 0 0 0 -66 0 l-6 34 c0 0 -18 -14 -18 -44 Z" fill="#6b3fa0" />
                {/* smile */}
                <path d="M46 84 q14 12 28 0" fill="none" stroke="#8a5a3a" strokeWidth="3" strokeLinecap="round" />
                {/* arm + phone */}
                <rect x="-40" y="196" width="70" height="26" rx="13" fill={SKIN_A} transform="rotate(-16 -5 209)" />
            </g>

            {/* phone in her hand — event feed */}
            <g transform="translate(250 300)">
                <rect x="0" y="0" width="120" height="196" rx="22" fill="#20223a" />
                <rect x="7" y="8" width="106" height="180" rx="16" fill="#fff" />
                <rect x="7" y="8" width="106" height="42" rx="16" fill="url(#home-hero-g)" />
                <rect x="18" y="22" width="60" height="7" rx="3.5" fill="#fff" opacity="0.9" />
                <rect x="18" y="34" width="40" height="6" rx="3" fill="#fff" opacity="0.6" />
                {[62, 96, 130].map((y) => (
                    <g key={y}>
                        <rect x="16" y={y} width="34" height="24" rx="6" fill={BLUE} opacity="0.25" />
                        <rect x="58" y={y + 2} width="46" height="7" rx="3.5" fill="#2b2b3a" opacity="0.75" />
                        <rect x="58" y={y + 13} width="34" height="6" rx="3" fill="#9aa0b4" />
                    </g>
                ))}
            </g>
        </svg>
    );
}

/* ─────────────────────── FEATURE ILLUSTRATIONS ─────────────────────────── */

// Reusable phone body (screen area is the children coordinate space 0..92 × 0..184).
function Phone({ children }: { children: React.ReactNode }) {
    return (
        <g>
            <rect x="0" y="0" width="108" height="200" rx="24" fill="#20223a" />
            <rect x="8" y="8" width="92" height="184" rx="16" fill="#fff" />
            <g transform="translate(8 8)">{children}</g>
        </g>
    );
}

/* Calendar — phone showing a month grid with a highlighted day + reminder bell. */
export function CalendarArt({ className }: ArtProps) {
    return (
        <svg viewBox="0 0 320 240" className={className} role="img" aria-label="Kalender">
            <defs>{grad('feat-cal-g')}</defs>
            <ellipse cx="160" cy="212" rx="120" ry="16" fill={PURPLE} opacity="0.08" />
            <g transform="translate(106 22)">
                <Phone>
                    <rect x="0" y="0" width="92" height="30" rx="10" fill="url(#feat-cal-g)" />
                    <rect x="12" y="10" width="46" height="8" rx="4" fill="#fff" opacity="0.9" />
                    {[0, 1, 2, 3].map((r) =>
                        [0, 1, 2, 3, 4, 5, 6].map((c) => {
                            const active = r === 1 && c === 3;
                            return (
                                <rect
                                    key={`${r}-${c}`}
                                    x={8 + c * 11.2}
                                    y={44 + r * 20}
                                    width="8.4"
                                    height="8.4"
                                    rx="2.4"
                                    fill={active ? 'url(#feat-cal-g)' : '#d7dcec'}
                                />
                            );
                        })
                    )}
                    <rect x="8" y="132" width="76" height="16" rx="6" fill={BLUE} opacity="0.16" />
                    <rect x="14" y="137" width="40" height="6" rx="3" fill="#2b2b3a" opacity="0.7" />
                    <rect x="8" y="154" width="76" height="16" rx="6" fill={PURPLE} opacity="0.16" />
                    <rect x="14" y="159" width="52" height="6" rx="3" fill="#2b2b3a" opacity="0.7" />
                </Phone>
            </g>
            {/* reminder bell badge */}
            <g transform="translate(196 30)">
                <circle cx="0" cy="0" r="26" fill="url(#feat-cal-g)" />
                <path d="M-9 4 a9 12 0 0 1 18 0 v3 h3 v3 h-24 v-3 h3 Z" fill="#fff" />
                <circle cx="0" cy="14" r="3.4" fill="#fff" />
            </g>
        </svg>
    );
}

/* Chat — two people (man + woman) with speech bubbles between them. */
export function ChatArt({ className }: ArtProps) {
    return (
        <svg viewBox="0 0 320 240" className={className} role="img" aria-label="Chat">
            <defs>{grad('feat-chat-g')}</defs>
            <ellipse cx="160" cy="214" rx="130" ry="16" fill={PURPLE} opacity="0.08" />
            {/* left person (man) */}
            <g transform="translate(28 96)">
                <rect x="0" y="70" width="78" height="110" rx="26" fill={BLUE} />
                <circle cx="39" cy="40" r="34" fill={SKIN_B} />
                <path d="M6 34 a33 33 0 0 1 66 0 v-5 a33 33 0 0 0 -66 0 Z" fill="#2f2114" />
            </g>
            {/* right person (woman) */}
            <g transform="translate(214 96)">
                <rect x="0" y="70" width="78" height="110" rx="26" fill="url(#feat-chat-g)" />
                <circle cx="39" cy="40" r="34" fill={SKIN_A} />
                <path d="M4 44 a35 35 0 0 1 70 0 c0 20 -8 28 -8 28 l-4 -22 a30 30 0 0 0 -46 0 l-4 22 c0 0 -8 -8 -8 -28 Z" fill="#6b3fa0" />
            </g>
            {/* bubbles */}
            <g>
                <rect x="112" y="66" width="96" height="40" rx="14" fill="#fff" stroke={BLUE} strokeWidth="2" />
                <path d="M126 106 l0 14 l18 -14 Z" fill="#fff" />
                {[132, 152, 172].map((x) => <circle key={x} cx={x} cy="86" r="4.4" fill={BLUE} />)}
            </g>
            <g>
                <rect x="120" y="120" width="88" height="36" rx="13" fill="url(#feat-chat-g)" />
                <path d="M192 156 l0 12 l-16 -12 Z" fill="url(#feat-chat-g)" />
                {[140, 158, 176].map((x) => <circle key={x} cx={x} cy="138" r="4.2" fill="#fff" />)}
            </g>
        </svg>
    );
}

/* Seat map — a screen with a seat grid, some seats taken, one being selected. */
export function SeatMapArt({ className }: ArtProps) {
    return (
        <svg viewBox="0 0 320 240" className={className} role="img" aria-label="Saalplan">
            <defs>{grad('feat-seat-g')}</defs>
            <rect x="44" y="30" width="232" height="164" rx="18" fill="#f2f0fb" stroke={BLUE} strokeWidth="2" />
            {/* stage */}
            <path d="M104 46 q56 -18 112 0 l0 12 q-56 -14 -112 0 Z" fill="url(#feat-seat-g)" />
            <text x="160" y="55" textAnchor="middle" fontFamily="Arial, sans-serif" fontSize="9" fontWeight="700" fill="#fff">STAGE</text>
            {/* seats */}
            {[0, 1, 2, 3].map((r) =>
                [0, 1, 2, 3, 4, 5, 6, 7].map((c) => {
                    const taken = (r + c) % 3 === 0;
                    const selected = r === 2 && c === 4;
                    return (
                        <rect
                            key={`${r}-${c}`}
                            x={70 + c * 22}
                            y={80 + r * 24}
                            width="15"
                            height="13"
                            rx="4"
                            fill={selected ? 'url(#feat-seat-g)' : taken ? '#c8cede' : '#fff'}
                            stroke={selected ? PURPLE : BLUE}
                            strokeWidth={selected ? 2.4 : 1.4}
                        />
                    );
                })
            )}
            {/* selection cursor */}
            <g transform="translate(150 118)">
                <path d="M0 0 l0 20 l6 -6 l5 10 l4 -2 l-5 -10 l8 0 Z" fill="#20223a" />
            </g>
        </svg>
    );
}

/* Community — a profile card with QR + a small cluster of followers/avatars. */
export function CommunityArt({ className }: ArtProps) {
    return (
        <svg viewBox="0 0 320 240" className={className} role="img" aria-label="Community">
            <defs>{grad('feat-com-g')}</defs>
            {/* profile card */}
            <rect x="40" y="34" width="150" height="172" rx="18" fill="#fff" stroke={BLUE} strokeWidth="2" />
            <rect x="40" y="34" width="150" height="52" rx="18" fill="url(#feat-com-g)" />
            <circle cx="80" cy="86" r="24" fill="#fff" />
            <circle cx="80" cy="80" r="9" fill={SKIN_A} />
            <path d="M64 100 a16 16 0 0 1 32 0 Z" fill={PURPLE} />
            <rect x="112" y="74" width="60" height="8" rx="4" fill="#2b2b3a" opacity="0.8" />
            <rect x="112" y="88" width="42" height="6" rx="3" fill="#9aa0b4" />
            {/* QR */}
            <rect x="60" y="122" width="66" height="66" rx="8" fill="#20223a" />
            {[0, 1, 2, 3, 4].map((r) =>
                [0, 1, 2, 3, 4].map((c) =>
                    (r * 7 + c * 3) % 2 === 0 ? (
                        <rect key={`${r}-${c}`} x={66 + c * 11} y={128 + r * 11} width="9" height="9" rx="1.5" fill="#fff" />
                    ) : null
                )
            )}
            <rect x="138" y="130" width="44" height="8" rx="4" fill={BLUE} opacity="0.3" />
            <rect x="138" y="146" width="44" height="8" rx="4" fill={PURPLE} opacity="0.3" />
            <rect x="138" y="162" width="30" height="8" rx="4" fill={BLUE} opacity="0.3" />
            {/* follower avatars cluster */}
            {[
                { x: 214, y: 70, f: BLUE },
                { x: 250, y: 100, f: PURPLE },
                { x: 224, y: 138, f: SKIN_B },
                { x: 262, y: 158, f: SKIN_A },
            ].map((a, i) => (
                <g key={i} transform={`translate(${a.x} ${a.y})`}>
                    <circle cx="0" cy="0" r="18" fill="#fff" stroke={BLUE} strokeWidth="1.6" />
                    <circle cx="0" cy="-4" r="6" fill={a.f} />
                    <path d="M-11 12 a11 11 0 0 1 22 0 Z" fill={a.f} />
                </g>
            ))}
        </svg>
    );
}

/* Free private events — an envelope invite with a lock (invite-only, free). */
export function PrivateEventArt({ className }: ArtProps) {
    return (
        <svg viewBox="0 0 320 240" className={className} role="img" aria-label="Private Events">
            <defs>{grad('feat-priv-g')}</defs>
            <ellipse cx="160" cy="212" rx="110" ry="15" fill={PURPLE} opacity="0.08" />
            {/* confetti */}
            {[
                { x: 70, y: 40, f: BLUE },
                { x: 250, y: 46, f: PURPLE },
                { x: 96, y: 74, f: PURPLE },
                { x: 232, y: 82, f: BLUE },
            ].map((c, i) => (
                <rect key={i} x={c.x} y={c.y} width="10" height="10" rx="2" transform={`rotate(${i * 25} ${c.x + 5} ${c.y + 5})`} fill={c.f} opacity="0.7" />
            ))}
            {/* envelope */}
            <rect x="86" y="70" width="148" height="104" rx="14" fill="#fff" stroke={BLUE} strokeWidth="2" />
            <path d="M86 84 L160 130 L234 84" fill="none" stroke={BLUE} strokeWidth="2" />
            <rect x="86" y="70" width="148" height="30" rx="14" fill="url(#feat-priv-g)" opacity="0.18" />
            {/* lock badge */}
            <g transform="translate(160 150)">
                <circle cx="0" cy="0" r="30" fill="url(#feat-priv-g)" />
                <rect x="-13" y="-2" width="26" height="22" rx="5" fill="#fff" />
                <path d="M-8 -2 v-6 a8 8 0 0 1 16 0 v6" fill="none" stroke="#fff" strokeWidth="4.5" />
                <circle cx="0" cy="8" r="3.4" fill="url(#feat-priv-g)" />
            </g>
            {/* free tag */}
            <g transform="translate(214 150)">
                <rect x="0" y="0" width="52" height="24" rx="12" fill={PURPLE} />
                <text x="26" y="16" textAnchor="middle" fontFamily="Arial, sans-serif" fontSize="11" fontWeight="800" fill="#fff">FREE</text>
            </g>
        </svg>
    );
}

/* Dashboard — a screen with a bar chart, a KPI ring and a QR check-in badge. */
export function DashboardArt({ className }: ArtProps) {
    return (
        <svg viewBox="0 0 320 240" className={className} role="img" aria-label="Dashboard">
            <defs>{grad('feat-dash-g')}</defs>
            <rect x="40" y="30" width="240" height="164" rx="18" fill="#fff" stroke={BLUE} strokeWidth="2" />
            <rect x="40" y="30" width="240" height="34" rx="18" fill="url(#feat-dash-g)" />
            <rect x="56" y="43" width="70" height="8" rx="4" fill="#fff" opacity="0.9" />
            {/* bars */}
            {[
                { x: 66, h: 40 },
                { x: 96, h: 64 },
                { x: 126, h: 30 },
                { x: 156, h: 78 },
            ].map((b, i) => (
                <rect key={i} x={b.x} y={172 - b.h} width="18" height={b.h} rx="5" fill="url(#feat-dash-g)" opacity={0.55 + i * 0.12} />
            ))}
            <line x1="60" y1="172" x2="188" y2="172" stroke="#d7dcec" strokeWidth="2" />
            {/* KPI ring */}
            <g transform="translate(236 120)">
                <circle cx="0" cy="0" r="30" fill="none" stroke="#e6e8f0" strokeWidth="9" />
                <circle cx="0" cy="0" r="30" fill="none" stroke="url(#feat-dash-g)" strokeWidth="9" strokeLinecap="round" strokeDasharray="140 190" transform="rotate(-90)" />
                <text x="0" y="5" textAnchor="middle" fontFamily="Arial, sans-serif" fontSize="15" fontWeight="800" fill={PURPLE}>74%</text>
            </g>
            {/* QR check-in badge */}
            <g transform="translate(224 188)">
                <rect x="0" y="0" width="56" height="34" rx="9" fill="#20223a" />
                <rect x="8" y="7" width="20" height="20" rx="3" fill="#fff" />
                <path d="M36 17 l6 7 l12 -14" fill="none" stroke="#5ad18a" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
            </g>
        </svg>
    );
}

/* ───────────────────────── APP-STORE BADGES ───────────────────────────── */
/* Conventional "Download on the App Store" / "Get it on Google Play" badges,
   drawn inline (the build blocks remote images) and linking to the real store
   listings. */

function AppStoreBadge() {
    return (
        <a
            className="se-store-badge"
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download on the App Store"
        >
            <svg viewBox="0 0 160 52" role="img" aria-hidden="true" focusable="false">
                <rect x="0.5" y="0.5" width="159" height="51" rx="10" fill="#000" stroke="#333" />
                <path
                    d="M35.5 27.4c0-3 2.5-4.5 2.6-4.6-1.4-2.1-3.6-2.3-4.4-2.4-1.9-.2-3.6 1.1-4.5 1.1-.9 0-2.4-1.1-3.9-1-2 0-3.9 1.2-4.9 3-2.1 3.6-.5 9 1.5 11.9 1 1.4 2.1 3 3.6 2.9 1.5-.1 2-.9 3.8-.9 1.8 0 2.2.9 3.8.9 1.6 0 2.6-1.4 3.6-2.9 1.1-1.6 1.6-3.2 1.6-3.3-.1 0-3-1.2-3-4.6zM32.6 18.4c.8-1 1.4-2.4 1.2-3.8-1.2 0-2.6.8-3.5 1.8-.7.9-1.4 2.3-1.2 3.6 1.3.1 2.7-.7 3.5-1.6z"
                    fill="#fff"
                />
                <text x="52" y="21" fontFamily="Arial, Helvetica, sans-serif" fontSize="8" fill="#fff">Download on the</text>
                <text x="52" y="38" fontFamily="Arial, Helvetica, sans-serif" fontSize="17" fontWeight="600" fill="#fff">App Store</text>
            </svg>
        </a>
    );
}

function PlayStoreBadge() {
    return (
        <a
            className="se-store-badge"
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Get it on Google Play"
        >
            <svg viewBox="0 0 160 52" role="img" aria-hidden="true" focusable="false">
                <rect x="0.5" y="0.5" width="159" height="51" rx="10" fill="#000" stroke="#333" />
                <g transform="translate(20 14)">
                    <path d="M0 0.6C0 .2.2 0 .5.2l13.7 12.5-3.6 3.6L.5 24.8C.2 25 0 24.8 0 24.4z" fill="#00D9FF" />
                    <path d="M.5.2 15.3 8.7l-4.7 4.3z" fill="#00F076" />
                    <path d="M15.3 16.3.5 24.8l10.1-8.5z" fill="#FF3A44" />
                    <path d="M15.3 8.7l4.9 2.8c1 .6 1 1.6 0 2.2l-4.9 2.6-4.7-3.8z" fill="#FFC800" />
                </g>
                <text x="52" y="21" fontFamily="Arial, Helvetica, sans-serif" fontSize="8" fill="#fff">GET IT ON</text>
                <text x="52" y="38" fontFamily="Arial, Helvetica, sans-serif" fontSize="16" fontWeight="600" fill="#fff">Google Play</text>
            </svg>
        </a>
    );
}

/** Row of both app-store badges (App Store + Google Play). */
export function StoreBadges({ className }: ArtProps) {
    return (
        <div className={`se-store-badges${className ? ` ${className}` : ''}`}>
            <AppStoreBadge />
            <PlayStoreBadge />
        </div>
    );
}
